import { AnimatePresence } from 'framer-motion'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button'
import Loader from '@/components/Loader'
import useCheckout from '@/features/Checkout/hooks/useCheckout'
import type { Product, ProductAvailability } from '@/lib/api.types'
import { useAvailabilityQuery } from '@/lib/queries'
import type { TicketPrice } from '@/types/tickets'

import AvailabilityModal from '../AvailabilityModal'
import DetailsModal from '../DetailsModal'
import LockedTicketModal from '../LockedTicketModal'
import ProductDescriptionPreview from '../ProductDescriptionPreview'
import ProductModal from '../ProductModal'
import TimeSlot from '../TimeSlot'
import {
  ProductCardBottom,
  ProductCardCategoryName,
  ProductCardCategoryPriceItem,
  ProductCardCategoryPrices,
  ProductCardContent,
  ProductCardDescription,
  ProductCardInfo,
  ProductCardLabels,
  ProductCardLockButton,
  ProductCardMedia,
  ProductCardMoreBadge,
  ProductCardMoreButton,
  ProductCardNoSlotsMessage,
  ProductCardPrice,
  ProductCardPricing,
  ProductCardSlots,
  ProductCardTitle,
  ProductCardTop,
  ProductCardWholeDay,
  ProductCardWholeDayHours,
  ProductCardWholeDayHoursLabel,
  ProductCardWholeDayHoursValue,
  ProductCardWrapper
} from './ProductCard.styles'

interface ProductHourSlot {
  time: string
  availability: number
  language: string | null
}

interface OpeningHours {
  start: string
  end: string
}

interface ProductSlotsData {
  availabilityType: 'TIME_SLOT' | 'WHOLE_DAY'
  slots: ProductHourSlot[]
  wholeDayAvailability: number | null
  openingHours: OpeningHours | null
}

const normalizePrice = (value: unknown) => {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return 0
  }

  return numericValue >= 0 ? numericValue : 0
}

const extractTicketPrice = (product: Product, fallbackCurrency?: string): TicketPrice => {
  const primaryCategory = product.consumerCategories?.[0] as
    | {
        price?: {
          grossAmount?: string
          currency?: string
          taxPercentage?: number
          tax?: {
            value?: string
          }
        }
        lowestPriceIn30Days?: {
          grossAmount?: string
        }
      }
    | undefined

  const grossAmount = normalizePrice(primaryCategory?.price?.grossAmount ?? product.price?.grossAmount ?? product.defaultPrice)
  const lowestGrossAmountIn30Days = normalizePrice(
    primaryCategory?.lowestPriceIn30Days?.grossAmount ?? primaryCategory?.price?.grossAmount ?? grossAmount
  )
  const taxPercentage = normalizePrice(
    primaryCategory?.price?.tax?.value ?? primaryCategory?.price?.taxPercentage ?? product.price?.taxPercentage
  )
  const currency = primaryCategory?.price?.currency || product.price?.currency || fallbackCurrency || 'PLN'

  return {
    grossAmount,
    currency,
    taxPercentage,
    lowestGrossAmountIn30Days
  }
}

const normalizeTime = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const match = value.match(/^(\d{1,2}):(\d{2})/)
  if (!match) {
    return null
  }

  return `${match[1].padStart(2, '0')}:${match[2]}`
}

const asObject = (value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null

const extractSlotsFromArray = (source: unknown[], fallbackAvailability: number): ProductHourSlot[] => {
  const slots = source
    .map(item => {
      if (typeof item === 'string') {
        const time = normalizeTime(item)
        return time ? { time, availability: fallbackAvailability, language: null } : null
      }

      const typedItem = asObject(item)
      if (!typedItem) {
        return null
      }

      const time = normalizeTime(typedItem.startTime ?? typedItem.time ?? typedItem.hour)
      if (!time) {
        return null
      }

      const maxCapacity = Number(typedItem.maxCapacity)
      const soldSeats = Number(typedItem.soldSeats)
      const explicitAvailability = Number(typedItem.availability ?? typedItem.availabilityCount ?? typedItem.availableSeats)

      let availability = fallbackAvailability
      if (Number.isFinite(explicitAvailability)) {
        availability = explicitAvailability
      } else if (Number.isFinite(maxCapacity) && Number.isFinite(soldSeats)) {
        availability = Math.max(0, maxCapacity - soldSeats)
      }

      const language = typeof typedItem.language === 'string' ? typedItem.language : null

      return { time, availability, language }
    })
    .filter((slot): slot is ProductHourSlot => Boolean(slot))
    .sort((a, b) => a.time.localeCompare(b.time))

  return slots
}

const resolveApiSlots = (availabilityData: ProductAvailability[] | undefined, fallbackAvailability: number): ProductSlotsData | null => {
  if (!Array.isArray(availabilityData) || availabilityData.length === 0) {
    return null
  }

  const typedEntries = availabilityData
    .map(item => asObject(item) as (ProductAvailability & Record<string, unknown>) | null)
    .filter((item): item is ProductAvailability & Record<string, unknown> => Boolean(item))

  if (typedEntries.length === 0) {
    return null
  }

  const firstTimeSlotEntry = typedEntries.find(
    entry => (entry.availabilityType === 'TIME_SLOT' || entry.hourlyAvailability !== null) && Array.isArray(entry.hourlyAvailability)
  )
  if (firstTimeSlotEntry && Array.isArray(firstTimeSlotEntry.hourlyAvailability)) {
    const slots = extractSlotsFromArray(firstTimeSlotEntry.hourlyAvailability, fallbackAvailability)
    return {
      availabilityType: 'TIME_SLOT',
      slots,
      wholeDayAvailability: null,
      openingHours: null
    }
  }

  const firstWholeDayEntry = typedEntries.find(entry => entry.availabilityType === 'WHOLE_DAY')
  if (firstWholeDayEntry) {
    const maxCapacity = Number(firstWholeDayEntry.maxCapacity)
    const soldSeats = Number(firstWholeDayEntry.soldSeats)
    const hasKnownCapacity = Number.isFinite(maxCapacity) && Number.isFinite(soldSeats)
    const wholeDayAvailability = hasKnownCapacity ? Math.max(0, maxCapacity - soldSeats) : null

    const entryRecord = firstWholeDayEntry as Record<string, unknown>
    const startTime = normalizeTime(entryRecord.startTime)
    const endTime = normalizeTime(entryRecord.endTime)
    const openingHours: OpeningHours | null = startTime && endTime ? { start: startTime, end: endTime } : null

    return {
      availabilityType: 'WHOLE_DAY',
      slots: [],
      wholeDayAvailability,
      openingHours
    }
  }

  return null
}

const extractProductSlots = (product: Product, availabilityData: ProductAvailability[] | undefined): ProductSlotsData | null => {
  const fallbackAvailability = Number(product.availabilityCount) > 0 ? Number(product.availabilityCount) : 2
  const apiSlots = resolveApiSlots(availabilityData, fallbackAvailability)
  if (apiSlots) {
    return apiSlots
  }

  const productRecord = product as unknown as Record<string, unknown>
  const additionalConfiguration = asObject(productRecord.additionalConfiguration)
  const nearestAvailability = asObject(productRecord.nearestAvailability)

  const configuredHours = additionalConfiguration?.availableHours
  if (Array.isArray(configuredHours)) {
    const slots = extractSlotsFromArray(configuredHours, fallbackAvailability)
    if (slots.length > 0) {
      return {
        availabilityType: 'TIME_SLOT',
        slots,
        wholeDayAvailability: null,
        openingHours: null
      }
    }
  }

  const hourlyAvailability = nearestAvailability?.hourlyAvailability
  if (Array.isArray(hourlyAvailability)) {
    const slots = extractSlotsFromArray(hourlyAvailability, fallbackAvailability)
    if (slots.length > 0) {
      return {
        availabilityType: 'TIME_SLOT',
        slots,
        wholeDayAvailability: null,
        openingHours: null
      }
    }
  }

  return null
}

const formatMoney = (value: number, keepTwoDecimals = true) => {
  if (keepTwoDecimals) {
    return value.toFixed(2)
  }

  return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

const formatCurrency = (currency: string) => (currency === 'PLN' ? 'zł' : currency)

const padDatePart = (value: number) => String(value).padStart(2, '0')
const getTodayDate = () => {
  const now = new Date()
  return `${now.getFullYear()}-${padDatePart(now.getMonth() + 1)}-${padDatePart(now.getDate())}`
}

interface ProductCardProps {
  product: Product
  categoryId: number | string | null
  vertical?: boolean
  currency?: string
}

const buildCartItemId = (productId: string, consumerCategoryId: number | string | null, date: string, startTime: string | null) => {
  const normalizedConsumerCategoryId = consumerCategoryId === null ? 'none' : String(consumerCategoryId)
  const normalizedStartTime = startTime ?? 'whole-day'
  return `ticket:${productId}:${normalizedConsumerCategoryId}:${date}:${normalizedStartTime}`
}

const ProductCard = ({ product, categoryId, vertical, currency: currencyFallback }: ProductCardProps) => {
  const { t } = useTranslation()
  const productIdForAvailability = product.id
  const id = Number(product.productId || product.id)
  const productId = String(product.id)
  const name = product.name
  const restricted = Boolean(product.requirePersonalData)
  const price = extractTicketPrice(product, currencyFallback)
  const consumerCategoryId = product.consumerCategories?.[0]?.id ?? null
  const paymentMethod = product.defaultPaymentMethod || 'onSite'
  const pickupMethod = product.defaultPickupMethod || 'onSite'
  const { grossAmount, currency } = price
  const displayCurrency = formatCurrency(currency)
  const sortedConsumerCategories = useMemo(
    () => [...(product.consumerCategories || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [product.consumerCategories]
  )
  const shouldShowCategoryPricePairs = sortedConsumerCategories.length > 2
  const visibleCategoryPrices = shouldShowCategoryPricePairs ? sortedConsumerCategories.slice(0, 2) : []
  const coverImageUrl = product.coverImage?.url || product.images?.[0]?.url || '/img/gallery01.jpg'
  const {
    data: availabilityData,
    isLoading: isAvailabilityLoading,
    isFetching: isAvailabilityFetching
  } = useAvailabilityQuery(categoryId, productIdForAvailability)
  const productSlots = useMemo(() => extractProductSlots(product, availabilityData), [product, availabilityData])
  const selectedDate = useMemo(() => {
    const firstAvailabilityDate = availabilityData?.find(item => typeof item?.date === 'string' && item.date.length > 0)?.date
    return firstAvailabilityDate || getTodayDate()
  }, [availabilityData])
  const isSlotsLoading = (isAvailabilityLoading || isAvailabilityFetching) && productSlots === null
  const availableSlots = productSlots?.slots ?? []
  const isWholeDayProduct = productSlots?.availabilityType === 'WHOLE_DAY'
  const isWholeDayAvailable = isWholeDayProduct
    ? productSlots?.wholeDayAvailability === null || productSlots?.wholeDayAvailability > 0
    : false
  const wholeDayOpeningHours = isWholeDayProduct ? (productSlots?.openingHours ?? null) : null
  const hasSelectableSlots = availableSlots.some(slot => slot.availability > 0)
  const visibleSlots = availableSlots.slice(0, 4)
  const remainingSlotsCount = Math.max(0, availableSlots.length - visibleSlots.length)
  const { addToCart, addUnlockedTicket, unlockedTickets, cartNotRestrictedQty } = useCheckout()
  const hasConsumerCategories = Array.isArray(product.consumerCategories) && product.consumerCategories.length > 0
  const [showLockedModal, setShowLockedModal] = useState(false)
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showProductModal, setShowProductModal] = useState(false)
  const [detailsSelectedTime, setDetailsSelectedTime] = useState<string | null>(null)
  const [detailsMaxTotalQty, setDetailsMaxTotalQty] = useState<number | null>(null)
  const isUnlocked = useMemo(() => unlockedTickets.includes(id) || cartNotRestrictedQty > 0, [unlockedTickets, id, cartNotRestrictedQty])
  const isLocked = !isUnlocked && restricted

  const handleUnlock = () => {
    setShowLockedModal(false)
    addUnlockedTicket(id)
  }

  const openDetailsModal = (selectedTime: string | null, maxTotalQty: number | null = null) => {
    setShowAvailabilityModal(false)
    setDetailsSelectedTime(selectedTime)
    setDetailsMaxTotalQty(maxTotalQty)
    setShowDetailsModal(true)
  }

  const handleHourSelect = (hour: string) => {
    if (isLocked) {
      toast.error(t('ticketLocked'))
      return
    }

    if (hasConsumerCategories) {
      const selectedSlot = availableSlots.find(slot => slot.time === hour)
      openDetailsModal(hour, selectedSlot?.availability ?? null)
      return
    }

    const cartItemId = buildCartItemId(productId, consumerCategoryId, selectedDate, hour)

    addToCart({
      id: cartItemId,
      productId,
      productName: name,
      coverImageUrl,
      name,
      price,
      qty: 1,
      consumerCategoryId,
      date: selectedDate,
      startTime: hour,
      pickupMethod,
      paymentMethod,
      orderItemComponents: product.productComponents ?? [],
      availabilityType: 'TIME_SLOT',
      unlockId: id,
      restricted
    })
  }

  const handleAvailabilityModalSelect = (hour: string) => {
    if (hasConsumerCategories) {
      if (isLocked) {
        toast.error(t('ticketLocked'))
        return
      }

      const selectedSlot = availableSlots.find(slot => slot.time === hour)
      openDetailsModal(hour, selectedSlot?.availability ?? null)
      return
    }

    handleHourSelect(hour)
  }

  const handleWholeDaySelect = () => {
    if (isLocked) {
      toast.error(t('ticketLocked'))
      return
    }

    if (hasConsumerCategories) {
      openDetailsModal(null, productSlots?.wholeDayAvailability ?? null)
      return
    }

    const cartItemId = buildCartItemId(productId, consumerCategoryId, selectedDate, null)

    addToCart({
      id: cartItemId,
      productId,
      productName: name,
      coverImageUrl,
      name,
      price,
      qty: 1,
      consumerCategoryId,
      date: selectedDate,
      startTime: null,
      pickupMethod,
      paymentMethod,
      orderItemComponents: product.productComponents ?? [],
      availabilityType: 'WHOLE_DAY',
      unlockId: id,
      restricted
    })
  }

  return (
    <>
      <ProductCardWrapper vertical={vertical}>
        <ProductCardMedia>
          <img src={coverImageUrl} alt={name} />
        </ProductCardMedia>
        <ProductCardContent>
          <ProductCardTop>
            <ProductCardInfo>
              <ProductCardTitle>{name}</ProductCardTitle>
              <ProductCardDescription>
                <ProductDescriptionPreview className="description-preview" product={product} onMore={() => setShowProductModal(true)} />
              </ProductCardDescription>
            </ProductCardInfo>
            {shouldShowCategoryPricePairs ? (
              <ProductCardCategoryPrices>
                {visibleCategoryPrices.map(category => {
                  const categoryCurrency = formatCurrency(category?.price?.currency || currency)
                  const categoryGrossAmount = normalizePrice(category?.price?.grossAmount)

                  return (
                    <ProductCardCategoryPriceItem key={category.id}>
                      <ProductCardCategoryName>{category.name}</ProductCardCategoryName>
                      <ProductCardPrice>
                        {formatMoney(categoryGrossAmount)} {categoryCurrency}
                      </ProductCardPrice>
                    </ProductCardCategoryPriceItem>
                  )
                })}
              </ProductCardCategoryPrices>
            ) : (
              <ProductCardPricing>
                <ProductCardPrice>
                  {formatMoney(grossAmount)} {displayCurrency}
                </ProductCardPrice>
              </ProductCardPricing>
            )}
          </ProductCardTop>

          {!isLocked && isSlotsLoading && (
            <ProductCardBottom>
              <Loader />
            </ProductCardBottom>
          )}

          {!isLocked && !isSlotsLoading && isWholeDayProduct && (
            <ProductCardWholeDay>
              {wholeDayOpeningHours && (
                <ProductCardWholeDayHours>
                  <ProductCardWholeDayHoursLabel>{t('openingHours')}</ProductCardWholeDayHoursLabel>
                  <ProductCardWholeDayHoursValue>
                    {wholeDayOpeningHours.start} – {wholeDayOpeningHours.end}
                  </ProductCardWholeDayHoursValue>
                </ProductCardWholeDayHours>
              )}
              <Button type="button" variant="tertiary" size="sm" disabled={!isWholeDayAvailable} onClick={handleWholeDaySelect}>
                {t('select')}
              </Button>
            </ProductCardWholeDay>
          )}

          {!isLocked && !isSlotsLoading && !isWholeDayProduct && !hasSelectableSlots && (
            <ProductCardBottom>
              <ProductCardNoSlotsMessage>{t('noTimeSlotsAvailable')}</ProductCardNoSlotsMessage>
            </ProductCardBottom>
          )}

          {!isLocked && !isSlotsLoading && !isWholeDayProduct && hasSelectableSlots && (
            <ProductCardBottom>
              <ProductCardLabels>
                <strong>{t('hourLabel')}</strong>
                <span>{t('ticketsAvailabilityLabel')}</span>
              </ProductCardLabels>
              <ProductCardSlots>
                {visibleSlots.map(slot => (
                  <TimeSlot
                    key={`${slot.time}-${slot.language ?? 'default'}`}
                    time={slot.time}
                    availability={slot.availability}
                    language={slot.language}
                    onClick={() => handleHourSelect(slot.time)}
                  />
                ))}
                {remainingSlotsCount > 0 && (
                  <ProductCardMoreButton type="button" onClick={() => setShowAvailabilityModal(true)}>
                    <span>{t('more')}</span>
                    <ProductCardMoreBadge>{remainingSlotsCount}</ProductCardMoreBadge>
                  </ProductCardMoreButton>
                )}
              </ProductCardSlots>
            </ProductCardBottom>
          )}

          {isLocked && (
            <ProductCardLockButton type="button" onClick={() => setShowLockedModal(true)}>
              {t('unlock')}
            </ProductCardLockButton>
          )}
        </ProductCardContent>
      </ProductCardWrapper>
      <AnimatePresence>
        {showAvailabilityModal && (
          <AvailabilityModal
            product={product}
            slots={availableSlots}
            selectedTime={null}
            onSelect={handleAvailabilityModalSelect}
            onClose={() => setShowAvailabilityModal(false)}
          />
        )}
        {showDetailsModal && (
          <DetailsModal
            selectedDate={selectedDate}
            selectedTime={detailsSelectedTime}
            product={product}
            currency={currencyFallback}
            maxTotalQty={detailsMaxTotalQty}
            openingHours={wholeDayOpeningHours}
            onClose={() => setShowDetailsModal(false)}
          />
        )}
        {showLockedModal && <LockedTicketModal onClose={() => setShowLockedModal(false)} onSuccess={handleUnlock} />}
      </AnimatePresence>
      <AnimatePresence>{showProductModal && <ProductModal product={product} onClose={() => setShowProductModal(false)} />}</AnimatePresence>
    </>
  )
}

export default ProductCard
